import { LeftOutlineIcon } from '@carboncopyco/icons';
import { Button, Separator, TextBase } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  capitalize,
  useAnalytics,
} from '@carboncopyco/utils';
import { RadioGroup } from '@headlessui/react';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import classNames from 'classnames';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screenshot } from '../Screenshot';

export type TabButtons<T extends string> = Record<
  T,
  { label: string; icon: ReactElement }
>;

export type InfoPageSidebarProps<T extends string> = {
  goBack: () => void;
  tabButtons: TabButtons<T>;
  screenshot?: string;
  rootPath: string;
  isOverlayDemo?: boolean;
  isOverlayTour?: boolean;
  isPlaybook?: boolean;
  isLoading?: boolean;
  handleChange?: (tabId: string) => void;
};

export function InfoPageSidebar<T extends string>({
  goBack,
  tabButtons,
  screenshot,
  rootPath,
  isOverlayDemo,
  isOverlayTour,
  isPlaybook,
  isLoading,
  handleChange,
}: InfoPageSidebarProps<T>) {
  const { tabId } = useParams<{ tabId: T }>();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  return (
    <div className="border-r w-ds-57 h-full flex flex-col border-ds-natural-150">
      <div className="px-2 pt-4 flex justify-start">
        <Button
          noPadding
          link
          dark
          label="Exit"
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.DEMO_CLICK_EXIT, {
              location: 'demo_info_page_top_actions',
            });
            goBack();
          }}
          leftIcon={<LeftOutlineIcon />}
        />
      </div>
      <div className="h-ds-166 p-6">
        <AspectRatio ratio={16 / 10}>
          {isLoading ? (
            <div className="bg-ds-natural-50 animate-pulse w-full h-full rounded" />
          ) : (
            <Screenshot
              image={screenshot}
              isOverlayDemo={isOverlayDemo}
              isOverlayTour={isOverlayTour}
              isPlaybook={isPlaybook}
            />
          )}
        </AspectRatio>
      </div>
      <Separator horizontal />
      <div className="p-6 grow">
        <RadioGroup
          value={tabId}
          onChange={(tabId) => {
            if (handleChange) {
              handleChange(tabId);
              return;
            }
            navigate(`${rootPath}/${tabId}`);
          }}
        >
          <div className="flex flex-col gap-2">
            {Object.keys(tabButtons).map((tabId) => {
              const { label, icon, disabled } = tabButtons[tabId];
              if (disabled) {
                return null;
              }
              return (
                <RadioGroup.Option
                  key={tabId}
                  value={tabId}
                >
                  {({ checked }) => (
                    <div
                      className={classNames(
                        'flex items-center w-full p-3 text-ds-primary-300 rounded-ds-md hover:cursor-pointer whitespace-nowrap',
                        { 'bg-ds-primary-50': checked },
                        {
                          'hover:bg-ds-natural-50': !checked,
                        }
                      )}
                    >
                      <div
                        className={classNames(
                          'mr-1.5',
                          checked
                            ? 'text-ds-primary-300'
                            : 'text-ds-natural-600'
                        )}
                      >
                        {icon}
                      </div>
                      <TextBase
                        label={capitalize(label)}
                        color={
                          checked
                            ? 'text-ds-primary-300'
                            : 'text-ds-natural-600'
                        }
                      />
                    </div>
                  )}
                </RadioGroup.Option>
              );
            })}
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}
